import type {ReactNode} from "react";
import {useAppDensity} from "../app/AppContext";

export type OtsSelectableListItem = {
  id: string;
  /** Card content: typically a line of metadata, a title, and status badges. */
  content: ReactNode;
  /** Overrides the accessible name when the content alone is ambiguous. */
  ariaLabel?: string;
  disabled?: boolean;
};

export type OtsSelectableListProps = {
  items: OtsSelectableListItem[];
  selectedId?: string;
  onSelect: (id: string) => void;
  ariaLabel: string;
  /** Tighter cards for embedded hosts. Defaults to the app density. */
  compact?: boolean;
  className?: string;
};

/**
 * A vertical list of cards where one is selected, such as a history of runs or versions beside their
 * details. Each card is a toggle button announcing whether it is the selected one.
 */
export const OtsSelectableList = ({items, selectedId, onSelect, ariaLabel, compact: compactProp, className}: OtsSelectableListProps) => {
  const density = useAppDensity();
  const compact = compactProp ?? density === "compact";
  return <ul aria-label={ariaLabel} data-density={compact ? "compact" : "default"}
    className={[compact ? "space-y-1.5" : "space-y-2", className].filter(Boolean).join(" ")}>
    {items.map(item => {
      const selected = item.id === selectedId;
      return <li key={item.id}>
        <button type="button" aria-pressed={selected} aria-label={item.ariaLabel} disabled={item.disabled}
          onClick={() => onSelect(item.id)}
          className={[
            "w-full rounded-xl border text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ots-primary)]",
            compact ? "px-2.5 py-2" : "p-3",
            selected
              ? "border-[var(--ots-primary)] bg-[var(--ots-primary-container)]"
              : "border-[var(--ots-outline-variant)] bg-[var(--ots-surface)] hover:bg-[var(--ots-surface-variant)]",
          ].join(" ")}>
          {item.content}
        </button>
      </li>;
    })}
  </ul>;
};
